import React, { useState, useEffect } from 'react';
import { Owner, deleteOwner, renameOwner, archiveOwner, fetchLoginStats, LoginStats, fetchVisitStats, VisitStats } from '../services/adminService';
import { FleetSensor } from '../types';
import { X, Trash2, ExternalLink, Pencil, Archive, ArchiveRestore, Check } from 'lucide-react';

interface Props {
  owners: Owner[];
  sensors: FleetSensor[];
  onClose: () => void;
  onChanged: () => void;
}

// Customer management. Three operations with very different consequences, so
// they read differently here:
//   rename   — safe, and never touches secret_link_id, which is the address of
//              a dashboard the customer may already have bookmarked
//   archive  — reversible, keeps everything, just gets them out of the way
//   delete   — irreversible, and refused server-side while they own devices
const CustomersModal: React.FC<Props> = ({ owners, sensors, onClose, onChanged }) => {
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [logins, setLogins] = useState<Record<string, LoginStats>>({});
  const [visits, setVisits] = useState<Record<string, VisitStats>>({});

  useEffect(() => {
    fetchLoginStats().then(rows => {
      setLogins(Object.fromEntries(rows.map(r => [r.userId, r])));
    });
    fetchVisitStats().then(rows => {
      setVisits(Object.fromEntries(rows.map(r => [r.userId, r])));
    });
  }, []);

  const sensorCount = (ownerId: string) => sensors.filter(s => s.userId === ownerId).length;
  const visible = owners.filter(o => showArchived || !o.archivedAt);
  const archivedCount = owners.filter(o => o.archivedAt).length;

  const run = async (fn: () => Promise<string | null>) => {
    setError(null);
    const message = await fn();
    if (message) { setError(message); return; }
    setConfirming(null);
    setRenaming(null);
    onChanged();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div
        className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-lg p-5 max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-100">Customers</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        {archivedCount > 0 && (
          <button
            onClick={() => setShowArchived(v => !v)}
            className="text-xs text-slate-400 hover:text-slate-200 mb-2"
          >
            {showArchived ? 'Hide' : 'Show'} {archivedCount} archived
          </button>
        )}

        {visible.length === 0 && <p className="text-slate-500 text-sm">No customers yet.</p>}

        <div className="divide-y divide-slate-700/60">
          {visible.map(o => {
            const count = sensorCount(o.id);
            const archived = !!o.archivedAt;
            return (
              <div key={o.id} className={`py-3 flex items-center gap-3 ${archived ? 'opacity-50' : ''}`}>
                <div className="min-w-0 flex-1">
                  {renaming === o.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        value={draftName}
                        onChange={(e) => setDraftName(e.target.value)}
                        autoFocus
                        className="flex-1 bg-slate-900 border border-slate-700 text-slate-200 rounded px-2 py-1 text-sm"
                      />
                      <button
                        onClick={() => run(() => renameOwner(o.id, draftName))}
                        className="p-1 text-emerald-400 hover:text-emerald-300"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button onClick={() => setRenaming(null)} className="text-xs text-slate-400">Cancel</button>
                    </div>
                  ) : (
                    <>
                      <div className="text-slate-200 font-medium truncate flex items-center gap-2">
                        {o.name}
                        {archived && <span className="text-[10px] uppercase tracking-wide text-slate-500">archived</span>}
                      </div>
                      <div className="text-xs text-slate-500">
                        {count} device{count === 1 ? '' : 's'}
                        {logins[o.id] && logins[o.id].totalLogins > 0 && (
                          <span className="ml-2">
                            · {logins[o.id].totalLogins} PIN entry{logins[o.id].totalLogins === 1 ? '' : 'ies'}
                          </span>
                        )}
                        {visits[o.id] && visits[o.id].totalVisits > 0 && (
                          <span className="ml-2">
                            · {visits[o.id].totalVisits} visit{visits[o.id].totalVisits === 1 ? '' : 's'}
                            {' '}({visits[o.id].visits30d} in 30d)
                            {visits[o.id].lastVisitAt && (
                              <> · last {new Date(visits[o.id].lastVisitAt as string).toLocaleDateString()}</>
                            )}
                          </span>
                        )}
                        {o.secretLinkId && (
                          <a
                            href={`#/view/${o.secretLinkId}`}
                            target="_blank"
                            rel="noreferrer"
                            className="ml-2 inline-flex items-center gap-1 text-sky-400 hover:underline"
                          >
                            open dashboard <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {renaming !== o.id && (
                  confirming === o.id ? (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => run(() => deleteOwner(o.id))}
                        className="px-2 py-1 text-xs bg-red-600 hover:bg-red-500 text-white rounded"
                      >
                        Confirm
                      </button>
                      <button onClick={() => setConfirming(null)} className="text-xs text-slate-400 hover:text-slate-200">
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => { setRenaming(o.id); setDraftName(o.name); }}
                        title="Rename"
                        className="p-1.5 text-slate-500 hover:text-sky-400"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => run(() => archiveOwner(o.id, !archived))}
                        title={archived ? 'Restore' : 'Archive — keeps all their data'}
                        className="p-1.5 text-slate-500 hover:text-amber-400"
                      >
                        {archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => setConfirming(o.id)}
                        disabled={count > 0}
                        title={count > 0 ? 'Reassign or delete their devices first' : 'Delete permanently'}
                        className="p-1.5 text-slate-500 hover:text-red-400 disabled:opacity-30 disabled:hover:text-slate-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>

        {error && <p className="text-sm text-red-400 mt-3">{error}</p>}
      </div>
    </div>
  );
};

export default CustomersModal;
